import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Nav } from "@/components/Nav";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, Download, Eye, ArrowRight, Upload, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function CustomerWelcome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [w9Url, setW9Url] = useState("");
  const [taxFile, setTaxFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [taxName, setTaxName] = useState(user?.tax_exempt_file?.filename || null);

  useEffect(() => {
    api.get("/config/w9").then(({ data }) => setW9Url(data.url)).catch(() => {});
  }, []);

  const uploadTax = async () => {
    if (!taxFile) { toast.error("Choose a PDF or image file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", taxFile);
      const { data } = await api.post("/customer/tax-exempt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTaxName(data.tax_exempt_file.filename);
      setTaxFile(null);
      toast.success("Tax Exempt Certificate uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <Nav />
      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">New Customer Welcome</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-[#0F172A]">
            Welcome to Peregrine!
          </h1>
          <p className="mt-4 text-base text-zinc-600 max-w-prose" data-testid="welcome-instructions">
            Please use this portal to add your trade credit references. Click the yellow "Complete the form" button to proceed.
          </p>

          {/* Editorial ribbon */}
          <div className="mt-8 bg-white border border-zinc-200 relative">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#D4AF37]" />
            <div className="p-8 sm:p-12 max-w-prose text-[15px] leading-relaxed text-zinc-700 space-y-5">
              <p>
                We are excited about this partnership. I would like to personally thank you for choosing us as your
                provider of Peregrine products and services.
              </p>
              <p>
                We look forward to building a meaningful relationship with you and your business and appreciate the
                opportunity to offer you the best products and services available. Peregrine is poised to provide you
                with outstanding customer service support and top-of-the-line products required for all your business
                needs.
              </p>
              <p className="text-[#0F172A] font-medium">Please review the welcome packet, which includes the following items:</p>
              <ul className="space-y-1.5 list-none">
                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-[#D4AF37]" /> Trade Reference / Credit Form</li>
                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 bg-[#D4AF37]" /> Peregrine's Form W-9</li>
              </ul>
              <p>
                Peregrine's focus is on <span className="font-medium text-[#0F172A]">Customer Service First</span>. This
                translates into world-class service and support for you, our Customer, so that you can be focused on
                your business' needs and goals.
              </p>
              <p>If you have any questions or comments about our products or services, please contact me at any time.</p>
              <div className="pt-4">
                <p className="text-zinc-700">Sincerely,</p>
                <p className="font-display font-bold text-xl text-[#0F172A] mt-1">Eric H. Fletcher</p>
                <p className="text-sm text-zinc-500">Owner · Peregrine Companies</p>
                <p className="text-sm text-zinc-500 font-mono mt-1">Direct: (318) 347-6512 · eric@palube.com</p>
              </div>
            </div>
          </div>

          {/* Action cards */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* W-9 */}
            <div className="bg-white border border-zinc-200 p-6 hover:-translate-y-0.5 hover:shadow-sm transition-transform">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-[#0F172A]/5 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-[#0F172A]" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-[#0F172A]">Peregrine Form W-9</h3>
                  <p className="text-xs text-zinc-500">View or download our tax form</p>
                </div>
              </div>
              <div className="mt-5 flex gap-3">
                <a
                  href={w9Url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="w9-view"
                  className="flex items-center gap-2 px-4 py-2.5 text-sm border border-[#0F172A] text-[#0F172A] hover:bg-zinc-100 transition-colors"
                >
                  <Eye className="h-4 w-4" /> View
                </a>
                <a
                  href={w9Url}
                  download
                  data-testid="w9-download"
                  className="flex items-center gap-2 px-4 py-2.5 text-sm bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors"
                >
                  <Download className="h-4 w-4" /> Download
                </a>
              </div>
            </div>

            {/* Credit form CTA */}
            <div className="bg-[#0F172A] text-white p-6 relative overflow-hidden hover:-translate-y-0.5 transition-transform">
              <div className="relative z-10">
                <h3 className="font-display font-bold text-lg">Trade / Credit Reference Form</h3>
                <p className="text-sm text-zinc-300 mt-1">
                  Apply for a line of credit by adding your trade references.
                </p>
                <button
                  onClick={() => navigate("/reference-form")}
                  data-testid="go-to-form"
                  className="group mt-5 flex items-center gap-2 bg-[#D4AF37] text-[#0F172A] px-4 py-2.5 text-sm font-medium hover:bg-[#e0bd48] transition-colors"
                >
                  Complete the form
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </div>

          {/* Tax Exempt Certificate upload */}
          <div className="mt-8 bg-white border border-zinc-200 p-6 sm:p-8" data-testid="tax-exempt-box">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-[#0F172A]/5 flex items-center justify-center">
                <FileText className="h-5 w-5 text-[#0F172A]" />
              </div>
              <div>
                <h3 className="font-display font-bold text-[#0F172A]">Tax Exempt Certificate</h3>
                <p className="text-xs text-zinc-500">If your business is tax exempt, upload your certificate here (PDF or image, max 10MB).</p>
              </div>
            </div>
            {taxName ? (
              <div className="mt-5 flex items-center gap-3 border border-[#10B981]/40 bg-[#10B981]/5 px-4 py-3 text-sm" data-testid="tax-exempt-uploaded">
                <CheckCircle2 className="h-4 w-4 text-[#10B981] shrink-0" />
                <span className="text-[#0F172A] flex-1 truncate">{taxName}</span>
                <a href={`${BACKEND}/api/customer/tax-exempt`} target="_blank" rel="noreferrer" data-testid="tax-exempt-view" className="text-xs text-[#0F172A] underline underline-offset-2 shrink-0">View</a>
                <button type="button" onClick={() => setTaxName(null)} data-testid="tax-exempt-replace" className="text-xs text-zinc-500 hover:text-[#0F172A] shrink-0">Replace</button>
              </div>
            ) : (
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <input
                  type="file"
                  accept="application/pdf,.pdf,image/*"
                  onChange={(e) => setTaxFile(e.target.files?.[0] || null)}
                  data-testid="tax-exempt-file"
                  className="block w-full text-sm text-zinc-600 file:mr-3 file:py-2.5 file:px-4 file:border-0 file:bg-[#0F172A] file:text-white file:text-sm file:cursor-pointer border border-zinc-300 p-1.5"
                />
                <button
                  type="button"
                  onClick={uploadTax}
                  disabled={uploading}
                  data-testid="tax-exempt-submit"
                  className="flex items-center justify-center gap-2 bg-[#0F172A] text-white px-6 py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> Upload Certificate</>}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
